import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import delaboLogo from '@/assets/images/Logo_DELABO_portal.svg';
import { AppTopNav } from '@/app/layout/AppTopNav';
import { PartnerLabInfo } from '@/app/layout/PartnerLabInfo';
import { UserMenu } from '@/app/layout/UserMenu';
import { resolveTopNavItems } from '@/app/layout/navItems';
import { usePrincipal } from '@/core/auth/appToken';
import { useOpenAssignmentCount } from '@/features/customers/api/useOpenAssignmentCount';
import { useDatextNavAction } from '@/features/abrechnung/hooks/useDatextNavAction';
import { useUngelesenZaehler } from '@/features/dateiablage/api/useUngelesenZaehler';

/**
 * "Header Top Row" + "Header Nav Row" of the portal shell chrome — organisation-type-agnostic,
 * so it later also carries the DELABO-Labor and Support contexts (only {@link PartnerLabInfo}
 * itself gates on customer org types; see its TSDoc). Both rows share the shell-wide
 * `max-w-[1440px]` centered content column (Pencil frames are all 1440px wide).
 *
 * Top Row layout is `justify-between`: the portal logo sits on the far left; on the right,
 * {@link PartnerLabInfo} is grouped ahead of {@link UserMenu} in reading order (left → right).
 * This is a **customer-approved deviation** from the Pencil design (`d2jH0`), which places the
 * partner-lab block on the far left and the user menu on the far right with a centered logo —
 * agreed with PO/customer instead (E01-03 "Design-Abweichung 1"). `AppHeader` composes both
 * components as-is and does not duplicate their internal gating/logic.
 *
 * Nav Row: the item set is resolved via {@link resolveTopNavItems}, a single organisation-type
 * based selector (#572) that replaced the former role-allowlist check — see its TSDoc in
 * `navItems.ts` for the exact set-per-`organizationType` matrix (three sets: customer, DELABO
 * Labor, DELABO Portal), the per-principal "Übersicht" target, the `/logistik` target of
 * "Logistik" for every recognized organisation type, and the `null`-principal (boot-window) fallback.
 *
 * The "Kunden" entry's open-Freigaben badge (#573) is fed by {@link useOpenAssignmentCount},
 * the same hook and query key the DELABO-Labor dashboard tile (`DashboardRoute`) already
 * consumes — TanStack Query dedupes the two consumers into a single request. On `isError` (403 for
 * a non-DELABO-lab role, or a network fault) the count is passed through as `undefined` rather
 * than a stale/last-known value, matching the dashboard tile's own error handling; `AppTopNav`
 * (and, inside it, `NavBadge`) already treats a missing count as "no badge". No client-side
 * organization/tenant parameter is ever sent — the hook's `enabled` gate is a fetch-avoidance
 * optimization only, the binding scope is the backend's `ActorContext` (see this item's Security
 * notes).
 *
 * The count is passed for every principal; which entry (if any) actually shows a badge is decided
 * by the item set, where only the DELABO-Labor "Kunden" entry declares `OPEN_FREIGABEN_BADGE`. A
 * Portal principal therefore never renders it, even though the same prop is handed over.
 *
 * The "Rechnungen / KVA" entry of the customer sets is a DATEXT action fed by
 * {@link useDatextNavAction}; it is rendered only while that hook reports `visible`.
 *
 * The "Dateiablage" entry's unread-documents badge (e16-02) follows the exact same pattern, fed by
 * {@link useUngelesenZaehler} instead: on `isError` the count is passed through as `undefined`
 * rather than a stale value, and the item set (not this component) decides that the Portal set's
 * "Dateiablage" entry never declares the badge — see `DATEIABLAGE_UNGELESEN_BADGE` in
 * `navItems.ts`.
 */
export function AppHeader() {
  const { t } = useTranslation('app');
  const principal = usePrincipal();
  const topNavItems = resolveTopNavItems(principal);
  const { data: openAssignmentCount, isError: isOpenAssignmentCountError } =
    useOpenAssignmentCount();
  const openFreigaben = isOpenAssignmentCountError ? undefined : openAssignmentCount?.count;
  const { data: ungelesenZaehler, isError: isUngelesenZaehlerError } = useUngelesenZaehler();
  const dateiablageUngelesen = isUngelesenZaehlerError ? undefined : ungelesenZaehler?.gesamt;
  const datextLinkout = useDatextNavAction();

  return (
    <header role="banner" className="border-b bg-background">
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-6">
        <NavLink to="/" end aria-label={t('portal.header.brand')}>
          <img src={delaboLogo} alt="" aria-hidden="true" className="h-10 w-auto" />
        </NavLink>

        <div className="flex items-center gap-6">
          <PartnerLabInfo />
          <UserMenu />
        </div>
      </div>

      <AppTopNav
        items={topNavItems}
        badges={{ openFreigaben, dateiablageUngelesen }}
        actions={{ datextLinkout }}
      />
    </header>
  );
}
